import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { useOpenAiGlobal } from "../use-openai-global";
import TabButton from "./TabButton";
import { LimitIcon } from "./icons";
import PlanCard from "./PlanCard";

function App() {
  const theme = useOpenAiGlobal("theme") || "light";

  const isDark = theme === "dark";

  const [currentTab, setCurrentTab] = useState("annual");
  const [plan, setPlan] = useState("PRO");
  const [maxFeatures, setMaxFeatures] = useState(() => {
    return window.innerWidth < 768 ? 2 : undefined;
  });

  useEffect(() => {
    function handleResize() {
      setMaxFeatures(window.innerWidth < 768 ? 2 : undefined);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      // Call your MCP tool to create Stripe checkout session
      const response = await window.openai.callTool("create-checkout-session", {
        subscription: currentTab.toUpperCase(),
        plan: plan,
      });
      const { checkout_url } = JSON.parse(response.result);
      // Redirect to Stripe
      window.openai.openExternal({ href: checkout_url });
    } catch (error) {
      console.error("Failed to create checkout:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`antialiased w-full overflow-hidden rounded-3xl ${
        isDark
          ? "bg-[#212121] border border-white-alpha-20"
          : "bg-white border border-black-alpha-15"
      }`}
      style={{ boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.05)" }}
    >
      {/* Header */}
      <div className={`flex flex-row items-center gap-16 pl-16 pr-8 py-6`}>
        <LimitIcon />
        <div className="flex flex-col">
          <p
            className={`font-medium text-[17px] leading-[1.35em] tracking-[-2.53%] ${
              isDark ? "text-white" : "text-[#0D0D0D]"
            }`}
          >
            Word limit reached
          </p>
          <p
            className={`font-regular text-[14px] leading-[1.42em] tracking-[-2.53%] ${
              isDark ? "text-[#CDCDCD]" : "text-[#5D5D5D]"
            }`}
          >
            Upgrade for more words and advanced features
          </p>
        </div>
      </div>
      {/* Tab Container */}
      <div
        className={`flex flex-row items-center ${
          isDark
            ? "border-b border-white-alpha-05"
            : "border-b border-black-alpha-05"
        }`}
      >
        <TabButton
          isActive={currentTab === "monthly"}
          onClick={() => setCurrentTab("monthly")}
          isDark={isDark}
        >
          Monthly
        </TabButton>
        <TabButton
          isActive={currentTab === "annual"}
          onClick={() => setCurrentTab("annual")}
          isDark={isDark}
        >
          <p>Annual</p>
          <div
            className={`py-2 px-8 rounded-full font-medium text-[12px] leading-[1.33em] tracking-[-2.53%] text-[#0D0D0D] ${
              isDark ? "bg-[#FFFFFF]" : "bg-[#0D0D0D1A]"
            }`}
          >
            Save 25%
          </div>
        </TabButton>
      </div>
      <div
        className={`flex flex-col md:flex-row gap-12 p-16 border-b ${
          isDark ? "border-white-alpha-05" : "border-black-alpha-05"
        }`}
      >
        <PlanCard
          planKey="PREMIUM"
          isActive={plan === "PREMIUM"}
          isMonthly={currentTab === "monthly"}
          isDark={isDark}
          onClick={() => setPlan("PREMIUM")}
          maxFeatures={maxFeatures}
        />
        <PlanCard
          planKey="PRO"
          isActive={plan === "PRO"}
          isMonthly={currentTab === "monthly"}
          isDark={isDark}
          onClick={() => setPlan("PRO")}
          maxFeatures={maxFeatures}
        />
      </div>
      <div className="p-16">
        <button
          onClick={handleSubscribe}
          disabled={isLoading}
          className={`px-24 py-10 w-full text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all bg-[#4D47DC] ${
            isDark
              ? "hover:bg-[#4D47DC]/90 active:bg-[#4D47DC]/80"
              : "hover:bg-[#4D47DC]/85 active:bg-[#4D47DC]/90"
          }`}
        >
          <span
            className={`font-medium text-[15px] leading-[1.6em] tracking-[-1.6%] ${
              isDark ? "text-white" : "text-[#FFFFFF]"
            }`}
          >
            {isLoading ? "Loading..." : "Choose Plan"}
          </span>
        </button>
      </div>
    </div>
  );
}

// Mount the app
const rootElement = document.getElementById("humanizer-plan-payment-root");
if (rootElement) {
  createRoot(rootElement).render(<App />);
}

export default App;
